import { Link } from "react-router-dom";
import { Shield, Zap, Bug, Lock, ArrowRight, CheckCircle2, ShieldAlert, ShieldCheck } from "lucide-react";

const protections = [
  {
    id: "waf",
    icon: Shield,
    title: "WAF (Web Application Firewall)",
    description: "OWASP Top 10 saldırılarına karşı kural tabanlı ve AI destekli koruma. SQL injection, XSS ve CSRF saldırılarını gerçek zamanlı engelleyin.",
    features: ["OWASP Top 10 koruması", "Özel kural motoru", "Bot yönetimi", "Rate limiting"],
  },
  {
    id: "ddos",
    icon: Zap,
    title: "DDoS Koruması",
    description: "1+ Tbps kapasiteye sahip ağımızla Layer 3, 4 ve 7 saldırılara karşı koruma. Saldırı tespiti ve otomatik önleme saniyeler içinde.",
    features: ["1 Tbps+ kapasite", "Saniyeler içinde müdahale", "Anycast yönlendirme", "Detaylı raporlama"],
  },
  {
    id: "malware",
    icon: Bug,
    title: "Malware Tarama",
    description: "Günlük otomatik tarama, şüpheli dosya izolasyonu ve temizleme. Truva atı, shell ve script enjeksiyonlarını tespit edin.",
    features: ["Günlük otomatik tarama", "Anlık karantina", "E-posta uyarıları", "Temizleme raporu"],
  },
  {
    id: "bruteforce",
    icon: Lock,
    title: "Brute-Force Koruması",
    description: "Yönetim panelleri, SSH ve e-posta sunucularına yönelik kaba kuvvet saldırılarını IP bazlı throttling ve CAPTCHA ile durdurun.",
    features: ["Otomatik IP engelleme", "Login rate limiting", "CAPTCHA entegrasyonu", "Coğrafi filtreleme"],
  },
];

export default function Security() {
  return (
    <div data-testid="security-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 lg:pt-24">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">Güvenlik</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Altyapınız için
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                çok katmanlı güvenlik.
              </span>
            </h1>
            <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
              WAF, DDoS koruması, malware taraması ve brute-force engelleme — kurumsal güvenlik
              standartları ISO 27001 ve SOC 2 ile uyumludur.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { v: "1+ Tbps", l: "DDoS kapasitesi" },
              { v: "< 2 sn", l: "Saldırı müdahale" },
              { v: "ISO 27001", l: "Sertifikalı" },
              { v: "SOC 2", l: "Type II hazır" },
            ].map((s, i) => (
              <div key={i} data-testid={`security-stat-${i}`} className="glass rounded-2xl p-6 card-hover">
                <ShieldCheck size={18} className="text-blue-400 mb-3" strokeWidth={1.6} />
                <div className="font-heading text-3xl font-semibold tracking-tight">{s.v}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-zinc-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROTECTIONS */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protections.map((p, i) => (
              <div key={p.id} id={p.id} data-testid={`protection-${p.id}`} className="card-hover glass rounded-3xl p-8 flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-700/10 border border-blue-500/20 flex items-center justify-center mb-6">
                  <p.icon size={26} strokeWidth={1.6} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-3">{p.title}</h3>
                <p className="text-zinc-400 leading-relaxed mb-6 flex-1">{p.description}</p>
                <ul className="space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                      <CheckCircle2 size={15} className="text-blue-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-white/5 bg-[#07080C]/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1220] to-[#0A0B10] p-10 lg:p-14">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <ShieldAlert size={28} className="text-blue-400 mb-4" />
                <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
                  Kurumsal güvenlik ihtiyacınız mı var?
                </h3>
                <p className="text-zinc-400 max-w-xl">
                  Güvenlik ekibimiz altyapınıza özel bir koruma paketi hazırlasın. Saatler içinde
                  devreye girer.
                </p>
              </div>
              <Link to="/iletisim" data-testid="security-cta-contact" className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white whitespace-nowrap transition-all">
                Teklif iste <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
